import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getAllegatiOrdine,
  uploadAllegatoOrdine,
  deleteAllegatoOrdine,
  downloadAllegatoOrdine,
} from '@/api/ordini'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Paperclip, Upload, Trash2, Download, FileText, Image as ImageIcon } from 'lucide-react'

const TIPI = [
  { value: 'contratto', label: 'Contratto' },
  { value: 'identita', label: 'Documento identità' },
  { value: 'altro', label: 'Altro' },
]

const MAX_MB = 20

function formatBytes(bytes) {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB']
  let i = 0
  let size = bytes
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024
    i += 1
  }
  return `${size.toFixed(1)} ${units[i]}`
}

function isImageMime(mime) {
  return (mime || '').startsWith('image/')
}

function AuthenticatedImagePreview({ ordineId, allegato }) {
  const [src, setSrc] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    let objectUrl = null

    setSrc(null)
    setFailed(false)
    downloadAllegatoOrdine(ordineId, allegato.id)
      .then((response) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(response.data)
        setSrc(objectUrl)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [ordineId, allegato.id])

  if (failed || !src) {
    return <ImageIcon className="h-8 w-8 text-muted-foreground" />
  }

  return (
    <img
      src={src}
      alt={allegato.descrizione || allegato.tipo}
      className="h-12 w-12 rounded object-cover border"
    />
  )
}

/**
 * Sezione "Allegati" nella pagina dettaglio ordine.
 * Punto 15 feedback cliente: possibilità di allegare una o più foto/PDF
 * del contratto firmato dal cliente.
 */
export default function AllegatiOrdine({ ordineId }) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef(null)
  const [tipo, setTipo] = useState('contratto')
  const [descrizione, setDescrizione] = useState('')
  const [downloadId, setDownloadId] = useState(null)

  const { data: allegati = [], isLoading } = useQuery({
    queryKey: ['allegati-ordine', ordineId],
    queryFn: () => getAllegatiOrdine(ordineId).then((r) => r.data),
    enabled: !!ordineId,
  })

  const uploadMutation = useMutation({
    mutationFn: (formData) => uploadAllegatoOrdine(ordineId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allegati-ordine', ordineId] })
      toast.success('Allegato caricato')
      setDescrizione('')
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    onError: (error) => {
      const data = error?.response?.data
      const msg = data?.file?.[0] || data?.detail || 'Errore durante il caricamento'
      toast.error(String(msg))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (allegatoId) => deleteAllegatoOrdine(ordineId, allegatoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allegati-ordine', ordineId] })
      toast.success('Allegato eliminato')
    },
    onError: () => toast.error('Errore durante l\'eliminazione'),
  })

  const handleUpload = (e) => {
    e.preventDefault()
    const file = fileInputRef.current?.files?.[0]
    if (!file) {
      toast.error('Seleziona un file')
      return
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Il file supera il limite di ${MAX_MB} MB`)
      return
    }
    const formData = new FormData()
    formData.append('file', file)
    formData.append('tipo', tipo)
    if (descrizione) formData.append('descrizione', descrizione)
    uploadMutation.mutate(formData)
  }

  const handleDelete = (allegato) => {
    if (confirm(`Eliminare l'allegato "${allegato.descrizione || allegato.tipo}"?`)) {
      deleteMutation.mutate(allegato.id)
    }
  }

  const handleDownload = async (allegato) => {
    setDownloadId(allegato.id)
    try {
      const response = await downloadAllegatoOrdine(ordineId, allegato.id)
      const objectUrl = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = allegato.nome_file || allegato.descrizione || 'allegato'
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(objectUrl)
    } catch {
      toast.error('Errore durante il download')
    } finally {
      setDownloadId(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Paperclip className="h-5 w-5" />
          Allegati
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Form upload */}
        <form onSubmit={handleUpload} className="grid gap-3 md:grid-cols-[1fr_auto_auto_auto] md:items-end">
          <div className="space-y-2">
            <Label htmlFor="allegato_file">File (max {MAX_MB} MB)</Label>
            <Input
              id="allegato_file"
              type="file"
              ref={fileInputRef}
              accept="image/*,application/pdf"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="allegato_tipo">Tipo</Label>
            <select
              id="allegato_tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
            >
              {TIPI.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="allegato_desc">Descrizione</Label>
            <Input
              id="allegato_desc"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder="Opzionale"
            />
          </div>
          <Button type="submit" disabled={uploadMutation.isPending}>
            <Upload className="h-4 w-4 mr-1" />
            {uploadMutation.isPending ? 'Caricamento...' : 'Carica'}
          </Button>
        </form>

        {/* Lista allegati */}
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Caricamento...</div>
        ) : allegati.length === 0 ? (
          <div className="text-sm text-muted-foreground">Nessun allegato.</div>
        ) : (
          <ul className="divide-y rounded-md border">
            {allegati.map((a) => (
              <li key={a.id} className="flex items-center gap-3 p-3">
                {isImageMime(a.mime_type) ? (
                  <AuthenticatedImagePreview ordineId={ordineId} allegato={a} />
                ) : (
                  <FileText className="h-8 w-8 text-muted-foreground" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {a.descrizione || a.tipo}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {a.tipo} • {formatBytes(a.dimensione_bytes)}
                    {a.caricato_da_username ? ` • ${a.caricato_da_username}` : ''}
                    {a.created_at ? ` • ${new Date(a.created_at).toLocaleString('it-IT')}` : ''}
                  </p>
                </div>
                {a.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(a)}
                    disabled={downloadId === a.id}
                    aria-label={`Scarica ${a.nome_file || a.descrizione || a.tipo}`}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(a)}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
